import type React from 'react';
import type { AppointmentController } from 'api';
import type {
  AppointmentStatus,
  AppointmentsMyBusinessDTOV2,
  PaginateAppointmentsDTO,
  PaginateVirtualToursV2DTO,
  Timezone,
} from '@bb/domain/dist/types';
import { BasicContact, EnhancedAppointment } from '@bb/domain/dist/types';
import type { Moment } from 'moment';
import type { openAppointmentModal as _openAppointmentModal } from 'utils';
import type { CovidAppointmentType } from '../../../app/covid19/COVID19AckTemplate';
import type { StatusText } from '../../components';

export type CalendarType = 'buyer' | 'la' | 'listing';

export const isCalendarType = (type: string): type is CalendarType =>
  ['buyer', 'la', 'listing'].includes(type);

/** Menu options for Agent (buyer or la) view of showings calendar */
export enum AgentCalendarMenu {
  Buyer = 'Buyer',
  Listing = 'Listing',
}

/** Menu options for Listing view of showings calendar */
export enum ListingCalendarMenu {
  InPerson = 'In Person',
  Virtual = 'Virtual',
}

export interface ShowingsCalendarPageParams {
  // Used in individual listing calendar
  listingId?: string;
  // Used optionally in buyer/la calendars
  page?: string;
}

export type ShowingCalendarSearchParams =
  | 'action'
  | 'agent'
  | 'appointmentId'
  | 'apptInfo'
  | 'dateFrom'
  | 'dateTo'
  | 'search'
  | 'status'
  | 'toast'
  | 'wait'
  | 'calendarSyncRedirect'
  | 'syncAppointmentIds'
  | 'syncListingIds';

export type StatusMenuItem = {
  label: StatusText;
  key: ShowingsCalendarAppointmentStatus;
};

export type TabPaneType = {
  key: ShowingCalendarMenuOption;
  label: JSX.Element;
};

export type ShowingCalendarMenuOption = AgentCalendarMenu | ListingCalendarMenu;

export type BuyerShowing = Awaited<
  ReturnType<(typeof AppointmentController)['getAppointmentsForCoop']>
>['appointments'][number];
type ListingShowing = AppointmentsMyBusinessDTOV2['appointments'][number];
type InPersonShowing = PaginateAppointmentsDTO['appointments'][number];
type VirtualShowing = PaginateVirtualToursV2DTO['virtualTours'][number];

// Modified versions of DTOs added by FE
export type BuyerTableData = (BuyerShowing & {
  passed?: boolean;
})[];
export type ListingTableData = (ListingShowing & {
  passed?: boolean;
})[];
export type InPersonTableData = (Omit<InPersonShowing, 'listing'> & {
  passed: boolean;
  listing: InPersonShowing['listing'] & { thumbnail: string };
})[];
export type VirtualTableData = (VirtualShowing & {
  date: string;
  listing: { thumbnail: string; timezone?: Timezone };
  originalDate?: undefined;
  originalTime?: undefined;
  time: string;
})[];

export type AppointmentTypes =
  | BuyerTableData
  | ListingTableData
  | InPersonTableData
  | VirtualTableData;

export type CommonInnerTableProps<
  T extends AppointmentTypes = AppointmentTypes,
> = {
  tableKey: string | number;
  showings: T;
  selectedCovidAppt?: CovidAppointmentType;
  setSelectedCovidAppt?: React.Dispatch<
    React.SetStateAction<CovidAppointmentType>
  >;
  isTourTable?: boolean;
  openAppointmentModal?: (
    ...[id, listingId, tab, options]: Parameters<typeof _openAppointmentModal>
  ) => void;
  isCovidSignWorking?: boolean;
};

export type OuterTableData = {
  date: string;
  time: string;
  showings: AppointmentTypes;
};

export type StatusCounts = Partial<
  Record<AppointmentStatus | 'total' | 'pending', number>
>;

export type ShowingsCalendarAppointmentStatus = AppointmentStatus | 'pending';

export type CalendarFilters = {
  searchText?: string;
  setSearchText?: React.Dispatch<React.SetStateAction<string>>;
  statuses?: AppointmentStatus[];
  setStatuses?: React.Dispatch<React.SetStateAction<AppointmentStatus[]>>;
  pendingStatus?: boolean;
  setPendingStatus?: React.Dispatch<React.SetStateAction<boolean>>;
  date?: [Moment, Moment | undefined];
  setDate?: React.Dispatch<React.SetStateAction<[Moment, Moment]>>;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
};

export type TableRenderFn<K> = (
  _: todo,
  appointment: K,
  i: number,
  opts?: { isMobile?: boolean },
) => JSX.Element;

export const isAppointmentStatus = (
  status: ShowingsCalendarAppointmentStatus,
): status is AppointmentStatus => status !== 'pending';

export type showingInfoType = {
  apptId: string;
  status: AppointmentStatus;
  orgCovidIncluded: boolean;
  apptCovidRequired: boolean;
  isAutoConfirm: boolean;
  listingLine1: string;
  listingLine2: string;
  durationHours: number;
  durationMin: number;
  timezone: Timezone;
  startTime: string;
  endTime: string;
};
